import React, { useMemo } from 'react';
import { useAuthNavigation } from '../../../../shared/navigation/useAuthNavigation';
import { View, Text, StyleSheet, Platform, type TextStyle } from 'react-native';
import type { ThemeColors } from '../../../../shared/theme';
import { appTheme } from '../../../../shared/theme';
import { Clickable, PRESS_PRESETS } from '../../../../components/common/Clickable';
import { SvgIcon } from '../../../../components/common/SvgIcon';
import { useTranslation } from '../../../../shared/i18n';
import { dashboardSet1CardShell, dashboardNestedTileShadow } from './dashboardSet1Styles';
import { DASH_STUDIO_INNER, DASH_STUDIO_RADIUS } from '../../../../shared/theme/studio/dashboardConstants';
import { useDashboardVariant } from '../../../../data/domains/config';

const ANDROID_TEXT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const ICON_SIZE = 24;
const TILE_GAP = appTheme.spacing.sm;

interface ActionItem {
  key: string;
  icon: string;
  labelKey: string;
  route: string;
  params?: object;
}

const ALL_ACTIONS: ActionItem[] = [
  { key: 'journey', icon: 'journey', labelKey: 'dashboard.journey', route: 'Journey', params: { from: 'dashboard' } },
  { key: 'subscriptions', icon: 'card', labelKey: 'dashboard.subscriptions', route: 'MySubscriptions' },
  { key: 'notifications', icon: 'bell', labelKey: 'dashboard.notifications', route: 'Notifications' },
  { key: 'consultations', icon: 'users', labelKey: 'dashboard.consultations', route: 'Consultation' },
  { key: 'loyalty', icon: 'trophy', labelKey: 'dashboard.rewards', route: 'Loyalty' },
  { key: 'addresses', icon: 'map', labelKey: 'dashboard.addresses', route: 'AddressLists' },
  { key: 'news', icon: 'news', labelKey: 'dashboard.news', route: 'News' },
  { key: 'community', icon: 'chat', labelKey: 'community.title', route: 'Community' },
  { key: 'settings', icon: 'settings', labelKey: 'dashboard.settings', route: 'Settings' },
];

function filterActions(
  show_community_action: boolean,
  show_consultations_action: boolean,
  show_loyalty_action: boolean,
  show_journey_action: boolean,
): ActionItem[] {
  return ALL_ACTIONS.filter(a => {
    if (a.key === 'journey' && !show_journey_action) {
      return false;
    }
    if (a.key === 'consultations' && !show_consultations_action) {
      return false;
    }
    if (a.key === 'loyalty' && !show_loyalty_action) {
      return false;
    }
    return (
      (a.key !== 'community' && a.key !== 'settings') ||
      (show_community_action && a.key === 'community') ||
      (!show_community_action && a.key === 'settings')
    );
  });
}

function ShortcutPill({
  action,
  label,
  colors,
  on_press,
}: {
  action: ActionItem;
  label: string;
  colors: ThemeColors;
  on_press: () => void;
}) {
  const pill_radius = DASH_STUDIO_RADIUS - 6;
  return (
    <View
      style={[
        styles.pill,
        dashboardNestedTileShadow(colors),
        {
          backgroundColor: colors.surfaceElevated,
          borderRadius: pill_radius,
        },
      ]}>
      <Clickable
        onPress={on_press}
        accessibilityLabel={label}
        accessibilityRole="button"
        pressScale={PRESS_PRESETS.listRow.scale}
        haptics
        contentBorderRadius={pill_radius}
        style={styles.pillPress}
        contentStyle={styles.pillInner}>
        <SvgIcon name={action.icon as any} size={ICON_SIZE} color={colors.primary} />
      </Clickable>
    </View>
  );
}

export interface DashboardQuickActionsSet1Props {
  colors: ThemeColors;
  show_community_action: boolean;
  show_consultations_action: boolean;
  show_loyalty_action: boolean;
  show_journey_action: boolean;
}

export function DashboardQuickActionsSet1({
  colors,
  show_community_action,
  show_consultations_action,
  show_loyalty_action,
  show_journey_action,
}: DashboardQuickActionsSet1Props) {
  const navigation = useAuthNavigation();
  const { t } = useTranslation();
  const variant = useDashboardVariant();
  const show_title = variant === 'set1';

  const actions = useMemo(
    () => filterActions(show_community_action, show_consultations_action, show_loyalty_action, show_journey_action),
    [show_community_action, show_consultations_action, show_loyalty_action, show_journey_action],
  );

  return (
    <View
      style={[
        dashboardSet1CardShell(colors),
        styles.card,
        { backgroundColor: colors.surface, overflow: 'visible' },
      ]}>
      {show_title ? (
        <Text style={[styles.overline, { color: colors.textLight }, ANDROID_TEXT]} accessibilityRole="header">
          {t('dashboard.sectionShortcuts')}
        </Text>
      ) : null}
      <View style={styles.wrap}>
        {actions.map(action => (
          <ShortcutPill
            key={action.key}
            action={action}
            label={t(action.labelKey)}
            colors={colors}
            on_press={() => navigation.navigate(action.route, action.params)}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: DASH_STUDIO_INNER,
    overflow: 'visible',
  },
  overline: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.9,
    textTransform: 'uppercase',
    marginBottom: appTheme.spacing.md,
  },
  wrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: TILE_GAP,
  },
  pill: {
    flexBasis: '30%',
    flexGrow: 1,
    minWidth: 0,
    overflow: 'visible',
  },
  pillPress: {
    width: '100%',
  },
  pillInner: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: appTheme.spacing.md,
    paddingHorizontal: appTheme.spacing.sm,
    minHeight: 64,
  },
});
